import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Trash2, UserPlus } from 'lucide-react';
import { api, unwrap } from '@/api/client';
import { Roles } from '@/auth/roles';
import { useAuth } from '@/auth/useAuth';
import { Badge } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/components/ui/dialog';
import { DataTable, EmptyRow, Field, RootError, Section } from '@/components/ui/form-controls';
import { Input } from '@/components/ui/input';
import { applyProblemToForm } from '@/lib/forms';
import { tenantKeys, useTenantUsers } from './api';
import { TENANT_ROLES } from './helpers';
import { userSchema, type UserForm } from './schemas';

export function UsersTab({ tenantId }: { tenantId: string }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const qc = useQueryClient();
  const users = useTenantUsers(tenantId);
  const [open, setOpen] = useState(false);

  const remove = useMutation({
    mutationFn: async (userId: string) =>
      unwrap(await api.DELETE('/v1/tenants/{id}/users/{userId}', { params: { path: { id: tenantId, userId } } })),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'users') });
      toast.success(t('admin.users.removed'));
    },
  });

  return (
    <Section
      title={t('admin.tenantTabs.users')}
      description={t('admin.users.description')}
      actions={
        <Button size="sm" onClick={() => setOpen(true)}>
          <UserPlus /> {t('admin.users.invite')}
        </Button>
      }
    >
      <DataTable head={[t('admin.users.name'), t('admin.users.email'), t('admin.users.roles'), t('admin.users.lastLogin'), '']}>
        {users.data?.items.length === 0 && <EmptyRow colSpan={5}>{t('admin.users.empty')}</EmptyRow>}
        {users.data?.items.map((u) => (
          <tr key={u.id}>
            <td>
              {u.displayName} {u.disabled && <Badge variant="destructive">{t('admin.users.disabled')}</Badge>}
            </td>
            <td>{u.email}</td>
            <td>
              <div className="flex flex-wrap gap-1">
                {u.roles.map((r) => (
                  <Badge key={r}>{t(`admin.roles.${r}`, { defaultValue: r })}</Badge>
                ))}
                {u.totpEnabled && <Badge variant="accent">2FA</Badge>}
              </div>
            </td>
            <td className="text-xs text-muted-foreground">{u.lastLoginAt ? new Date(u.lastLoginAt).toLocaleString() : '—'}</td>
            <td className="text-right">
              <Button
                size="sm"
                variant="ghost"
                aria-label={t('admin.users.remove', { name: u.displayName })}
                disabled={u.id === user?.id || remove.isPending}
                onClick={() => {
                  if (window.confirm(t('admin.users.confirmRemove', { name: u.displayName }))) remove.mutate(u.id);
                }}
              >
                <Trash2 />
              </Button>
            </td>
          </tr>
        ))}
      </DataTable>
      {open && <InviteUserDialog tenantId={tenantId} onClose={() => setOpen(false)} />}
    </Section>
  );
}

function InviteUserDialog({ tenantId, onClose }: { tenantId: string; onClose: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const form = useForm<UserForm>({
    resolver: zodResolver(userSchema(t)),
    defaultValues: { email: '', displayName: '', password: '', roles: [Roles.TenantReviewer] },
  });
  const create = useMutation({
    meta: { silent: true },
    mutationFn: async (v: UserForm) => unwrap(await api.POST('/v1/tenants/{id}/users', { params: { path: { id: tenantId } }, body: v })),
  });
  const onSubmit = form.handleSubmit(async (v) => {
    try {
      await create.mutateAsync(v);
      await qc.invalidateQueries({ queryKey: tenantKeys.part(tenantId, 'users') });
      toast.success(t('admin.users.invited'));
      onClose();
    } catch (err) {
      applyProblemToForm(form, err, { fields: ['email', 'displayName', 'password', 'roles'] });
    }
  });
  const { errors, isSubmitting } = form.formState;
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent>
        <DialogTitle>{t('admin.users.invite')}</DialogTitle>
        <DialogDescription>{t('admin.users.inviteHint')}</DialogDescription>
        <form className="mt-3 flex flex-col gap-3" onSubmit={(e) => void onSubmit(e)} noValidate>
          <Field label={t('admin.users.email')} htmlFor="u-email" error={errors.email?.message}>
            <Input id="u-email" type="email" aria-invalid={!!errors.email} {...form.register('email')} />
          </Field>
          <Field label={t('admin.users.name')} htmlFor="u-name" error={errors.displayName?.message}>
            <Input id="u-name" aria-invalid={!!errors.displayName} {...form.register('displayName')} />
          </Field>
          <Field label={t('admin.users.initialPassword')} htmlFor="u-pw" error={errors.password?.message}>
            <Input id="u-pw" type="password" autoComplete="new-password" aria-invalid={!!errors.password} {...form.register('password')} />
          </Field>
          <Controller
            control={form.control}
            name="roles"
            render={({ field }) => (
              <fieldset className="flex flex-col gap-1">
                <legend className="mb-1 text-xs font-medium">{t('admin.users.roles')}</legend>
                {TENANT_ROLES.map((r) => (
                  <label key={r} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={field.value.includes(r)}
                      onChange={(e) => field.onChange(e.target.checked ? [...field.value, r] : field.value.filter((x) => x !== r))}
                    />
                    {t(`admin.roles.${r}`)}
                  </label>
                ))}
                {errors.roles?.message && <p className="text-xs text-destructive">{errors.roles.message}</p>}
              </fieldset>
            )}
          />
          <RootError message={errors.root?.message} />
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('admin.common.cancel')}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {t('admin.users.invite')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
